import { Link, useMatches, useRouteError } from 'react-router-dom'
import { buttonClass } from '@/shared/ui/primitives'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Panel } from '@/shared/ui/Panel'
import { SipayLogo } from '@/shared/ui/Brand'

/**
 * A page of its own, outside any shell: the logo for bearings, then the panel,
 * centred on the grey page. There is no menu here, so the panel's title is the
 * page's h1.
 */
function StandaloneFrame({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-8 px-4 py-10">
      <SipayLogo className="h-9 dark:brightness-0 dark:invert" />
      {children}
    </main>
  )
}

/** The code, large and quiet: it is for reference, the sentence under it is for reading. */
function ErrorCode({ code }: { code: string }) {
  return (
    // A step brighter in the dark theme: gray-300 there is almost the card itself.
    <div className="text-5xl font-bold tracking-tight text-gray-300 tabular-nums dark:text-gray-400">
      {code}
    </div>
  )
}

/** Never a blank screen and never a redirect loop: always a way back. */
function ErrorShell({
  code,
  title,
  message,
  nested = false,
}: {
  code: string
  title: string
  message: string
  /** Inside a module shell, which already draws the heading and the page frame. */
  nested?: boolean
}) {
  const panel = (
    <Panel as="div" className="w-full max-w-md px-6 py-10 text-center sm:px-10">
      <ErrorCode code={code} />
      {/* Inside a shell the title is already the page's h1, right above;
          saying it again in the panel would repeat the same line twice. */}
      {!nested && <h1 className="mt-3 text-lg font-bold tracking-tight text-gray-900">{title}</h1>}
      <p className="mx-auto mt-2 max-w-sm leading-relaxed text-gray-600">{message}</p>
      <Link to="/" className={buttonClass('primary', 'md', 'mt-7')}>
        Kembali ke Beranda
      </Link>
    </Panel>
  )

  if (!nested) return <StandaloneFrame>{panel}</StandaloneFrame>
  return <div className="flex justify-center pt-4 sm:pt-10">{panel}</div>
}

export function ForbiddenPage() {
  return (
    <ErrorShell
      code="403"
      title="Tidak memiliki akses"
      message="Peran Anda tidak diberi akses ke halaman ini. Hubungi administrator bila menurut Anda ini keliru."
    />
  )
}

export function NotFoundPage() {
  // Also rendered as the catch-all child of a module, inside its shell. There
  // the shell already provides the page frame and the h1, so this only fills
  // the heading and draws the panel; standalone, it is a whole page.
  const nested = useMatches().length > 1
  const title = 'Halaman tidak ditemukan'
  return (
    <>
      {nested && <PageHeader title={title} />}
      <ErrorShell
        code="404"
        title={title}
        message="Alamat yang Anda tuju tidak ada atau sudah dipindahkan."
        nested={nested}
      />
    </>
  )
}

/**
 * The last line of defence: an uncaught render error.
 *
 * Without this the router shows its own developer screen -- a red stack trace
 * addressed to us, on a user's monitor. The technical detail is kept, but
 * folded away and labelled as something to send to IT rather than something to
 * read.
 */
export function RouteErrorPage() {
  const error = useRouteError()
  const detail =
    error instanceof Error ? `${error.name}: ${error.message}` : String(error ?? 'Unknown error')

  return (
    <StandaloneFrame>
      <Panel as="div" className="w-full max-w-md">
        <div className="px-6 pt-10 pb-8 text-center sm:px-10">
          <ErrorCode code="!" />
          <h1 className="mt-3 text-lg font-bold tracking-tight text-gray-900">Terjadi kesalahan</h1>
          <p className="mx-auto mt-2 max-w-sm leading-relaxed text-gray-600">
            Halaman gagal ditampilkan. Muat ulang untuk mencoba lagi; isian yang belum sempat
            disimpan tidak ikut terbawa.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-2">
            <button type="button" onClick={() => window.location.reload()} className={buttonClass('primary')}>
              Muat Ulang
            </button>
            <Link to="/" className={buttonClass('light')}>
              Beranda
            </Link>
          </div>
        </div>

        <details className="border-t border-line px-6 py-4 sm:px-10">
          <summary className="cursor-pointer text-gray-600">Keterangan untuk tim IT</summary>
          <pre className="mt-3 overflow-x-auto rounded-md bg-gray-100 p-3 text-xs text-gray-700">{detail}</pre>
        </details>
      </Panel>
    </StandaloneFrame>
  )
}
